import { useState, type SetStateAction } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import Logo from "../../assets/images/Logo.png";

export default function App() {
  const [expandedIndex, setExpandedIndex] = useState(-1);

  const faqs = [
    {
      question: "What Can I Order On Smartly Orders?",
      answer:
        "Smartly Orders lets you order delicious meals, groceries, everyday essentials, and exclusive Mystery Bags from your favourite restaurants, supermarkets, bakeries, and local stores—all through one convenient platform.",
    },
    {
      question: "What Are Mystery Bags?",
      answer:
        "Mystery Bags contain perfectly good surplus food from restaurants, bakeries, and stores at heavily discounted prices. The exact contents are a surprise, helping you save money while reducing food waste.",
    },
    {
      question: "How Do I Place An Order?",
      answer:
        "Simply browse nearby restaurants or stores, select your items or a Mystery Bag, choose delivery or pickup, complete your secure payment, and track your order until it reaches you.",
    },
  ];

  const toggleExpand = (index: SetStateAction<number>) => {
    setExpandedIndex(expandedIndex === index ? -1 : index);
  };

  return (
    <motion.div
      className="bg-[#e8e9d5] flex items-center justify-center p-4 md:p-8 overflow-hidden"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true }}
    >
      <div className="max-w-7xl w-full grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
        {/* Left side - Animated Illustration */}
        <motion.div
          className="flex justify-center items-center scale-[0.65] sm:scale-75 md:scale-100 origin-center -my-16 md:my-0"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <motion.div
            className="relative"
            animate={{ y: [0, -10, 0] }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
          >
            {/* Cactus */}
            <div className="absolute bottom-0 left-0 w-24 h-32">
              <div className="w-12 h-24 bg-emerald-600 rounded-t-full mx-auto"></div>
              <div className="w-8 h-12 bg-emerald-600 rounded-full absolute top-8 -left-2"></div>
              <div className="w-8 h-12 bg-emerald-600 rounded-full absolute top-12 right-0"></div>
              <div className="w-16 h-6 bg-emerald-600 rounded-b-lg absolute bottom-0 left-1/2 -translate-x-1/2"></div>
            </div>

            {/* Phone */}
            <div className="w-72 h-125 bg-[#1a1a1a] rounded-[3rem] p-4 shadow-2xl relative z-10 ml-32">
              <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden flex flex-col items-center justify-center p-6">
                <div className="w-24 h-24 rounded-full flex items-center justify-center">
                  <img src={Logo} alt="Smartly Orders" />
                </div>
                <p className="text-emerald-600 text-lg font-medium tracking-wider">
                  Smartly Orders
                </p>
                <div className="w-20 h-1 bg-emerald-600 mb-6 rounded-full"></div>
              </div>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-8 bg-[#1a1a1a] rounded-b-3xl"></div>
            </div>

            {/* Character */}
            <div className="absolute top-1/4 -right-12 z-20">
              <motion.div
                className="relative"
                animate={{ y: [0, -6, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 3,
                  ease: "easeInOut",
                }}
              >
                <div className="absolute -top-8 -right-4 w-12 h-12 bg-[#b8d4b8] rounded-full flex items-center justify-center">
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
                    <div className="w-1.5 h-1.5 bg-emerald-600 rounded-full"></div>
                  </div>
                </div>
                <div className="w-16 h-16 bg-[#f4b8a0] rounded-full relative">
                  <div className="absolute top-4 left-3 w-2 h-2 bg-[#1a1a1a] rounded-full"></div>
                  <div className="absolute top-4 right-3 w-2 h-2 bg-[#1a1a1a] rounded-full"></div>
                  <div className="absolute top-8 left-1/2 -translate-x-1/2 w-4 h-1 bg-[#d89a80] rounded-full"></div>
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-8 bg-[#1a1a1a] rounded-t-full"></div>
                </div>
                <div className="w-20 h-24 bg-emerald-600 rounded-t-3xl mt-1"></div>
                <div className="absolute top-16 -left-4 w-12 h-6 bg-emerald-600 rounded-full -rotate-45"></div>
                <div className="absolute top-16 -right-4 w-12 h-6 bg-emerald-600 rounded-full rotate-45"></div>
              </motion.div>
            </div>

            <div className="absolute bottom-12 -right-8">
              <div className="w-12 h-16 bg-[#b8d4b8] rounded-full rotate-45 opacity-60"></div>
              <div className="w-8 h-12 bg-[#b8d4b8] rounded-full -rotate-12 absolute top-4 left-4 opacity-60"></div>
            </div>
          </motion.div>
        </motion.div>

        {/* Right side - FAQ */}
        <motion.div
          className="flex flex-col justify-center min-h-100 md:h-auto py-8 md:py-0"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-5xl lg:text-6xl text-wrap text-center font-bold text-[#1a1a1a] mb-4">
            What To Know?
          </h2>
          <div className="w-24 h-1 bg-green-800 mb-12 mx-auto"></div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b-2 border-[#2d5a4a] pb-4">
                <button
                  onClick={() => toggleExpand(index)}
                  className="w-full flex items-center justify-between text-left group"
                >
                  <span className="text-lg font-medium text-[#1a1a1a] group-hover:text-emerald-500 transition-colors">
                    {faq.question}
                  </span>
                  {expandedIndex === index ? (
                    <ChevronUp className="w-6 h-6 text-[#2d5a4a] shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-[#2d5a4a] shrink-0" />
                  )}
                </button>

                <AnimatePresence>
                  {expandedIndex === index && (
                    <motion.p
                      className="mt-4 text-[#1a1a1a] leading-relaxed"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    >
                      {faq.answer}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
